import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';

/**
 * Vigilancia de sesión para usuarios del panel (admin/staff/viewer/cashier/qrScanner).
 *
 * - Mantiene un listener a `users/{uid}.authDisabled` para forzar logout si un
 *   admin deshabilita remotamente al usuario.
 * - Refresca periódicamente el ID token (fuerza revalidación contra Firebase).
 * - Al tab visible, vuelve a revalidar el token inmediatamente.
 * - Centraliza el limpiado de las claves de localStorage usadas por el panel,
 *   para que no queden flags "zombies" si un flujo se olvida de limpiar todo.
 *
 * Claves manejadas (AllegraClub):
 *   isAuthenticated, isAdmin, userRole, allowedFeatures
 */

const LS_KEYS = ['isAuthenticated', 'isAdmin', 'userRole', 'allowedFeatures'];

export function clearStaffSessionStorage() {
  LS_KEYS.forEach((k) => {
    try {
      localStorage.removeItem(k);
    } catch (_) {
      /* noop */
    }
  });
}

/**
 * Cierra sesión Firebase, limpia flags del panel y redirige a /login.
 * `reason` se propaga como query param para que la pantalla de login pueda
 * avisar ("session", "disabled", etc.) si quiere.
 */
export async function forceSignOutStaff(reason = '') {
  clearStaffSessionStorage();
  try {
    await signOut(auth);
  } catch (_) {
    /* ignore */
  }
  if (typeof window === 'undefined') return;
  const path = window.location.pathname || '';
  if (path.startsWith('/login')) return;
  const q = reason ? `?reason=${encodeURIComponent(reason)}` : '';
  window.location.replace(`/login${q}`);
}

const TOKEN_CHECK_MS = 45 * 1000;

/**
 * Inicia la vigilancia. Devuelve un "dispose" para detenerla.
 * Solo actúa si hay sesión de panel marcada en localStorage (isAuthenticated).
 */
export function initStaffSessionWatch() {
  let unsubDoc = null;
  let intervalId = null;

  const stopDoc = () => {
    if (unsubDoc) {
      unsubDoc();
      unsubDoc = null;
    }
  };

  const stopInterval = () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };

  const hasPanelSession = () => {
    try {
      return localStorage.getItem('isAuthenticated') === 'true';
    } catch (_) {
      return false;
    }
  };

  const refreshTokenOrKick = () => {
    const u = auth.currentUser;
    if (!u || !hasPanelSession()) return;
    u.getIdToken(true).catch(() => {
      forceSignOutStaff('session');
    });
  };

  const onVisibility = () => {
    if (document.visibilityState === 'visible') refreshTokenOrKick();
  };

  const unsubAuth = onAuthStateChanged(auth, (user) => {
    stopDoc();
    stopInterval();

    // Si Firebase perdió al usuario pero localStorage dice "autenticado",
    // limpiamos para no dejar flags zombies exponiendo UI de admin.
    if (!user) {
      if (hasPanelSession()) {
        clearStaffSessionStorage();
      }
      return;
    }

    if (!hasPanelSession()) return;

    const ref = doc(db, 'users', user.uid);
    unsubDoc = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data() || {};
        if (data.authDisabled === true) {
          forceSignOutStaff('disabled');
        }
      },
      () => {
        /* noop: errores transitorios de Firestore no deben matar la sesión */
      }
    );

    refreshTokenOrKick();
    intervalId = setInterval(refreshTokenOrKick, TOKEN_CHECK_MS);
  });

  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    unsubAuth();
    stopDoc();
    stopInterval();
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
